/**
 * Boomfection Landing — Campaign Module
 * Campaign level rendering and start handlers
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 *
 * XSS-SAFE: All innerHTML content below uses only hardcoded level data (not user
 * input). String values are additionally passed through escapeHtml() as defense
 * in depth. Numeric values (level.id, level.players, level.stars) are integers
 * from a const array. This code is moved verbatim from landing.js (pure refactor).
 *
 * Progress persistence: Logged-in users get server-side persistence via
 * /api/v1/campaign/progress. localStorage serves as fallback for guests.
 */

import { t } from '/js/i18n.js';

/** @type {{completedLevels: number[], levelStars: Object<number,number>, levelScores: Object<number,number>}|null} */
let serverProgress = null;

/**
 * Holt den Auth-Token aus localStorage
 * @returns {string|null}
 */
function getAccessToken() {
    return localStorage.getItem('accessToken') || null;
}

/**
 * Laedt den Campaign-Fortschritt vom Server (fuer eingeloggte User)
 * @returns {Promise<void>}
 */
async function fetchServerProgress() {
    const token = getAccessToken();
    if (!token) {
        serverProgress = null;
        return;
    }

    try {
        const res = await fetch('/api/v1/campaign/progress', {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
            serverProgress = null;
            return;
        }
        const data = await res.json();
        const completedLevels = [];
        const levelStars = {};
        const levelScores = {};

        for (const level of data.levels || []) {
            if (level.completed) completedLevels.push(level.levelId);
            levelStars[level.levelId] = level.stars || 0;
            levelScores[level.levelId] = level.bestScore || 0;
        }

        serverProgress = { completedLevels, levelStars, levelScores };

        // Merge in localStorage damit Offline-Fallback aktuell bleibt
        mergeServerToLocal(serverProgress);
    } catch (err) {
        console.warn('[LandingCampaign] Could not fetch server progress:', err);
        serverProgress = null;
    }
}

/**
 * Merged Server-Daten ins localStorage (best-of-both)
 * @param {{completedLevels: number[], levelStars: Object, levelScores: Object}} server
 */
function mergeServerToLocal(server) {
    try {
        let local = { completedLevels: [], currentLevel: 1 };
        const raw = localStorage.getItem('boomfection_campaign_progress');
        if (raw) local = JSON.parse(raw);

        const mergedCompleted = [...new Set([...(local.completedLevels || []), ...server.completedLevels])];
        local.completedLevels = mergedCompleted;
        local.currentLevel = mergedCompleted.length > 0 ? Math.max(...mergedCompleted) + 1 : 1;

        localStorage.setItem('boomfection_campaign_progress', JSON.stringify(local));
    } catch {
        /* ignore — localStorage may be unavailable (private mode, quota exceeded) */
    }
}

/**
 * Sendet einen abgeschlossenen Level an den Server (fire-and-forget)
 * @param {number} levelId
 * @param {number} stars
 * @param {number} score
 */
export function saveCampaignComplete(levelId, stars = 0, score = 0) {
    const token = getAccessToken();
    if (!token) return;

    fetch('/api/v1/campaign/complete', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ levelId, stars, score })
    }).catch(() => {
        /* silent - localStorage ist Fallback */
    });
}

/**
 * Renders campaign levels and attaches start handlers.
 * @param {Object} client - MultiplayerClient instance
 * @param {string} selectedColor - currently selected player color ID
 * @param {string} selectedVirusType - currently selected virus type ID
 */
export async function renderCampaignLevels(client, selectedColor, selectedVirusType) {
    const container = document.getElementById('campaign-levels');
    if (!container) return;

    // Server-Fortschritt laden (fuer eingeloggte User)
    await fetchServerProgress();

    let progress = { completedLevels: [], currentLevel: 1 };
    try {
        const data = localStorage.getItem('boomfection_campaign_progress');
        if (data) progress = JSON.parse(data);
    } catch {
        /* expected — localStorage unavailable or corrupt JSON; use defaults */
    }

    // Server-Daten haben Prioritaet: merge completed levels
    if (serverProgress) {
        const merged = [...new Set([...(progress.completedLevels || []), ...serverProgress.completedLevels])];
        progress.completedLevels = merged;
    }

    const levels = [
        { id: 1, name: t('campaign.level1.name'), desc: t('campaign.level1.desc'), ai: 'easy', subdivision: 2, players: 2, stars: 1 },
        { id: 2, name: t('campaign.level2.name'), desc: t('campaign.level2.desc'), ai: 'easy', subdivision: 2, players: 3, stars: 1 },
        { id: 3, name: t('campaign.level3.name'), desc: t('campaign.level3.desc'), ai: 'normal', subdivision: 3, players: 2, stars: 2 },
        { id: 4, name: t('campaign.level4.name'), desc: t('campaign.level4.desc'), ai: 'normal', subdivision: 3, players: 2, stars: 2 },
        { id: 5, name: t('campaign.level5.name'), desc: t('campaign.level5.desc'), ai: 'normal', subdivision: 3, players: 4, stars: 2 },
        { id: 6, name: t('campaign.level6.name'), desc: t('campaign.level6.desc'), ai: 'medium', subdivision: 3, players: 2, stars: 3 },
        { id: 7, name: t('campaign.level7.name'), desc: t('campaign.level7.desc'), ai: 'medium', subdivision: 4, players: 4, stars: 3 },
        { id: 8, name: t('campaign.level8.name'), desc: t('campaign.level8.desc'), ai: 'medium', subdivision: 4, players: 6, stars: 3 },
        { id: 9, name: t('campaign.level9.name'), desc: t('campaign.level9.desc'), ai: 'hard', subdivision: 3, players: 2, stars: 4 },
        { id: 10, name: t('campaign.level10.name'), desc: t('campaign.level10.desc'), ai: 'insane', subdivision: 3, players: 2, stars: 5 }
    ];

    // All values in the template are from the hardcoded `levels` array above
    // plus escapeHtml() for defense-in-depth on string fields
    container.textContent = '';
    const fragment = document.createDocumentFragment();

    levels.forEach(function (level) {
        const isCompleted = (progress.completedLevels || []).includes(level.id);
        const isUnlocked = level.id === 1 || (progress.completedLevels || []).includes(level.id - 1);

        const levelDiv = document.createElement('div');
        levelDiv.className = `campaign-level${isCompleted ? ' completed' : ''}${isUnlocked ? '' : ' locked'}`;
        levelDiv.dataset.levelId = level.id;

        // Header
        const header = document.createElement('div');
        header.className = 'campaign-level-header';
        const numSpan = document.createElement('span');
        numSpan.className = 'campaign-level-num';
        numSpan.textContent = `#${level.id}`;
        const nameSpan = document.createElement('span');
        nameSpan.className = 'campaign-level-name';
        nameSpan.textContent = level.name;
        header.appendChild(numSpan);
        header.appendChild(nameSpan);
        if (isCompleted) {
            const checkSpan = document.createElement('span');
            checkSpan.className = 'campaign-check';
            checkSpan.textContent = '\u2705';
            header.appendChild(checkSpan);
        }
        levelDiv.appendChild(header);

        // Description
        const descDiv = document.createElement('div');
        descDiv.className = 'campaign-level-desc';
        descDiv.textContent = level.desc;
        levelDiv.appendChild(descDiv);

        // Meta (stars + info)
        const metaDiv = document.createElement('div');
        metaDiv.className = 'campaign-level-meta';
        const starsSpan = document.createElement('span');
        starsSpan.className = 'campaign-level-stars';
        for (let i = 0; i < 5; i++) {
            const star = document.createElement('span');
            star.className = `campaign-star${i < level.stars ? ' active' : ''}`;
            star.textContent = '\u2B50';
            starsSpan.appendChild(star);
        }
        const infoSpan = document.createElement('span');
        infoSpan.className = 'campaign-level-info';
        infoSpan.textContent = t('campaign.levelInfo', { players: level.players, ai: t(`ai.${level.ai}`) || level.ai });
        metaDiv.appendChild(starsSpan);
        metaDiv.appendChild(infoSpan);
        levelDiv.appendChild(metaDiv);

        // Buttons
        if ((isUnlocked && !isCompleted) || isCompleted) {
            const btn = document.createElement('button');
            btn.className = isCompleted
                ? 'btn btn-sm btn-ghost campaign-start-btn'
                : 'btn btn-sm btn-primary campaign-start-btn';
            btn.dataset.level = level.id;
            btn.textContent = isCompleted ? t('campaign.playAgain') : t('campaign.start');
            btn.onclick = function () {
                const playerName = document.getElementById('create-name')?.value || t('game.defaultPlayerName');
                const aiSlots = [];
                for (let i = 1; i < level.players; i++) {
                    aiSlots.push({
                        index: i,
                        type: 'ai',
                        aiDifficulty: level.ai
                    });
                }
                client.createGame({
                    playerName,
                    colorId: selectedColor,
                    virusType: selectedVirusType,
                    maxPlayers: level.players,
                    subdivisions: level.subdivision,
                    isPublic: false,
                    pillsEnabled: level.id === 6,
                    slots: [{ index: 0, type: 'player', colorId: selectedColor }].concat(aiSlots)
                });
            };
            levelDiv.appendChild(btn);
        }

        if (!isUnlocked) {
            const lockedOverlay = document.createElement('div');
            lockedOverlay.className = 'campaign-locked-overlay';
            lockedOverlay.textContent = t('campaign.locked');
            levelDiv.appendChild(lockedOverlay);
        }

        fragment.appendChild(levelDiv);
    });

    container.appendChild(fragment);
}
